"use client"

import { useMutation } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import type { ChannelOfflineMessageConfig } from "@/lib/agents/messaging/types"

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok) {
    throw new Error(typeof data.error === "string" ? data.error : "Channel request failed")
  }
  return data
}

export function useStartMetaMessagingChannelMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: {
      agentId: string
      channelType: "instagram" | "facebook"
    }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/channels/${input.channelType}/auth`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agentId: input.agentId }),
        },
      )
      return readJson(response)
    },
  })
}

export function useConnectSimpleMessagingChannelMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: {
      agentId: string
      channelType: "web-widget" | "web-demo"
    }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/channels/${input.channelType}/connect`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agentId: input.agentId }),
        },
      )
      return readJson(response)
    },
  })
}

export function useConnectTelegramMessagingChannelMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: { agentId: string; botToken: string }) => {
      const response = await fetchWithAuth(
        "/api/agents/messaging/channels/telegram/connect",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        },
      )
      return readJson(response)
    },
  })
}

export function useDeleteMessagingChannelMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: { agentId: string; channelId: string }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/${input.agentId}/channels/${input.channelId}`,
        { method: "DELETE" },
      )
      return readJson(response)
    },
  })
}

export function useUpdateMessagingChannelAiRepliesMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: {
      agentId: string
      channelId: string
      aiRepliesEnabled: boolean
    }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/${input.agentId}/channels/${input.channelId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ai_replies_enabled: input.aiRepliesEnabled }),
        },
      )
      return readJson(response)
    },
  })
}

export function useUpdateMessagingChannelOfflineMessageMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: {
      agentId: string
      channelId: string
      offlineMessage: ChannelOfflineMessageConfig
    }) => {
      const response = await fetchWithAuth(
        `/api/agents/messaging/${input.agentId}/channels/${input.channelId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ offline_message: input.offlineMessage }),
        },
      )
      return readJson(response)
    },
  })
}

export function useUpdateWebWidgetPrimaryColorMutation() {
  const { fetchWithAuth } = useAuth()
  return useMutation({
    mutationFn: async (input: {
      channelId: string
      primaryColor: string
    }) => {
      const response = await fetchWithAuth(
        "/api/agents/messaging/channels/web-widget/config",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            channelId: input.channelId,
            config: { primaryColor: input.primaryColor },
          }),
        },
      )
      return readJson(response)
    },
  })
}
